import styled from "styled-components";

const RatingWrapper = styled.div`
  display: flex;
  justify-content: ${(props) => (props.$inline ? "flex-start" : "center")};
  margin: ${(props) => (props.$inline ? "0" : "16px 0 8px")};
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: ${(props) => (props.$inline ? "flex-start" : "center")};
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  padding: 6px;
  line-height: 0;
  border-radius: 50%;
  transition: transform 0.15s ease, opacity 0.15s ease;

  opacity: ${(props) =>
    props.disabled ? 0.35 : props.$selected ? 1 : 0.4};
  transform: ${(props) => (props.$selected ? "scale(1.12)" : "scale(1)")};

  &:hover {
    opacity: ${(props) => (props.disabled ? 0.35 : 1)};
    transform: ${(props) =>
      props.disabled ? "scale(1)" : props.$selected ? "scale(1.12)" : "scale(1.08)"};
  }

  &:focus-visible {
    outline: 2px solid #9e2480;
    outline-offset: 2px;
  }
`;

const SvgIcon = styled.svg`
  width: 44px;
  height: 44px;
  fill: currentColor;
`;

const customIcons = {
  1: {
    label: "Muito baixo",
    color: "#d32f2f",
    path: "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8m0-6c-2.33 0-4.32 1.45-5.12 3.5h1.67c.69-1.19 1.97-2 3.45-2s2.75.81 3.45 2h1.67c-.8-2.05-2.79-3.5-5.12-3.5",
  },
  2: {
    label: "Baixo",
    color: "#e53935",
    path: "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8m0-3.5c.73 0 1.39.19 1.97.53.12-.14.86-.98 1.01-1.14-.85-.56-1.87-.89-2.98-.89s-2.13.33-2.99.88c.97 1.09.01.02 1.01 1.14.59-.33 1.25-.52 1.98-.52",
  },
  3: {
    label: "Médio",
    color: "#ed6c02",
    path: "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8m0-4c-.73 0-1.38-.18-1.96-.52-.12.14-.86.98-1.01 1.15.86.55 1.87.87 2.97.87 1.11 0 2.12-.33 2.98-.88-.97-1.09-.01-.02-1.01-1.15-.59.35-1.24.53-1.97.53",
  },
  4: {
    label: "Alto",
    color: "#2e7d32",
    path: "M12 16c-1.48 0-2.75-.81-3.45-2H6.88c.8 2.05 2.79 3.5 5.12 3.5s4.32-1.45 5.12-3.5h-1.67c-.69 1.19-1.97 2-3.45 2m-.01-14C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8",
  },
  5: {
    label: "Muito alto",
    color: "#1b5e20",
    path: "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8m-5-6c.78 2.34 2.72 4 5 4s4.22-1.66 5-4z",
  },
};

const SentimentIcon = ({ icon, selected }) => (
  <SvgIcon
    viewBox="0 0 24 24"
    aria-hidden="true"
    style={{ color: selected ? icon.color : "#bdbdbd" }}
  >
    <circle cx="15.5" cy="9.5" r="1.5" />
    <circle cx="8.5" cy="9.5" r="1.5" />
    <path d={icon.path} />
  </SvgIcon>
);

const RatingScale = ({
  value,
  onChange,
  name = "rating-scale",
  max = 5,
  disabled = false,
  inline = false,
}) => {
  const numericValue =
    value !== undefined && value !== null && value !== ""
      ? Number(value)
      : null;

  const options = [];
  for (let i = 1; i <= max; i += 1) options.push(i);

  return (
    <RatingWrapper $inline={inline}>
      <RatingRow $inline={inline} role="radiogroup" aria-label="Classificação de preparo">
        {options.map((score) => {
          const icon = customIcons[score];
          const selected = numericValue === score;

          return (
            <IconButton
              key={score}
              type="button"
              role="radio"
              name={name}
              disabled={disabled}
              aria-checked={selected}
              aria-label={icon.label}
              title={icon.label}
              $selected={selected}
              onClick={() => !disabled && onChange(score)}
            >
              <SentimentIcon icon={icon} selected={selected} />
            </IconButton>
          );
        })}
      </RatingRow>
    </RatingWrapper>
  );
};

export default RatingScale;
